import { useBranchTimezone } from "../api/use-branch-timezone";
import { useEffect, useState } from "react";
import type {
  PatternDay,
  QuickFillSources,
  StaffDayKind,
  WeeklyPattern,
} from "@dentacore/shared-types";
import { STAFF_DAY_KINDS } from "@dentacore/shared-types";

import { readPattern } from "../api/rota";
import { describe } from "../settings/use-writer";
import { Modal } from "../ui/modal";
import { HelpTip } from "../ui/popovers";
import { describeNumberError, NumberField } from "../ui/number-field";
import type { NumberFieldRules } from "../ui/number-field";
import { hoursOf, localToday, minutesOf, shiftText } from "./roster";
import type { Person } from "./roster";
import styles from "./staff.module.css";
import { KIND_LABELS, WEEKDAY_NAMES } from "./weekdays";
import { RequiredMark } from "../ui/required-mark";

/** Zero to eight hours, matching the rota API. Not a default. */
const BREAK: NumberFieldRules = { min: 0, max: 480, integer: true, unit: "minutes" };

/**
 * One person's repeating week, as a dialog drawn like the page (ST13,
 * STORY-090, working plan §6.4).
 *
 * **Seven lines, each day's hours a pill; a day opens its editor under it.**
 * Only one is open at a time, so the week stays readable while one day is
 * being typed.
 *
 * **Nothing is written until Save.** Every save opens a new version (S1), so
 * the three fills change the form and nothing else — trying one costs nothing.
 *
 * **The total is the server's** once saved; while there are unsaved edits it
 * is the same §6.4 arithmetic done here, and says it is not saved yet.
 */
export function EveryWeekDialog({
  person,
  pattern,
  sources,
  busy,
  onSave,
  onRevert,
  onError,
  onClose,
}: {
  person: Person;
  pattern: WeeklyPattern;
  /** What this week can be filled from (STORY-091). Null until it has arrived. */
  sources: QuickFillSources | null;
  busy: boolean;
  onSave: (days: PatternDay[], effectiveFrom: string) => void;
  onRevert: () => void;
  onError: (message: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  /**
   * A week never set opens on the clinic's default working day (ST3–ST5),
   * **as unsaved edits** — only Save writes it.
   */
  const [days, setDays] = useState<PatternDay[]>(pattern.suggestedDays ?? pattern.days);
  const timezone = useBranchTimezone();
  const [effectiveFrom, setEffectiveFrom] = useState(() => localToday(timezone));
  const [open, setOpen] = useState<number | null>(null);
  const [copying, setCopying] = useState(false);

  // A save that came back — take the server's word for it.
  useEffect(() => {
    setDays(pattern.suggestedDays ?? pattern.days);
    setBreakDrafts({});
  }, [pattern]);

  const dirty = JSON.stringify(days) !== JSON.stringify(pattern.days);
  const suggested = pattern.suggestedDays !== null;

  /**
   * What is typed in each break box, beside the model rather than in it
   * (QA20-13): the model is written only when the text parses, so `2e` stays
   * on screen to be seen and fixed rather than saved as zero.
   */
  const [breakDrafts, setBreakDrafts] = useState<Record<number, string>>({});
  const breakTextOf = (day: PatternDay): string =>
    breakDrafts[day.dayOfWeek] ?? String(day.breakMinutes);
  const breakBlocked = days.some((day) => describeNumberError(breakTextOf(day), BREAK) !== null);

  function edit(dayOfWeek: number, change: Partial<PatternDay>): void {
    setDays((current) =>
      current.map((day) => (day.dayOfWeek === dayOfWeek ? { ...day, ...change } : day)),
    );
  }

  function editBreak(dayOfWeek: number, next: string): void {
    setBreakDrafts((current) => ({ ...current, [dayOfWeek]: next }));
    if (describeNumberError(next, BREAK) === null) {
      edit(dayOfWeek, { breakMinutes: next.trim() === "" ? 0 : Number(next) });
    }
  }

  /** A fill replaces the form; break drafts would otherwise outlive the days they were typed on. */
  function fill(next: PatternDay[]): void {
    setBreakDrafts({});
    setDays(next);
  }

  /**
   * Stopping work clears the hours — a stale 09:00–17:00 on a day marked off
   * is how old hours come back as paid time when it is switched on again. A
   * day given hours it did not have opens on its own last hours, or the
   * person's first worked day, rather than on a blank that saves as nothing.
   */
  function changeKind(day: PatternDay, kind: StaffDayKind): void {
    if (kind === "OFF") {
      setBreakDrafts((current) => ({ ...current, [day.dayOfWeek]: "0" }));
      edit(day.dayOfWeek, { kind, startsAt: null, endsAt: null, breakMinutes: 0 });
      return;
    }
    const model = days.find((row) => row.kind !== "OFF" && row.startsAt !== null);
    edit(day.dayOfWeek, {
      kind,
      startsAt: day.startsAt ?? model?.startsAt ?? null,
      endsAt: day.endsAt ?? model?.endsAt ?? null,
    });
  }

  const firstWorked = days.find((day) => day.kind !== "OFF");

  /**
   * The location's own week, each day keeping its own break — a branch has no
   * opinion on somebody's unpaid break (S3).
   */
  function fillOpeningHours(): void {
    if (sources?.openingHours == null) return;
    const byDay = new Map(sources.openingHours.map((day) => [day.dayOfWeek, day]));
    fill(
      days.map((day) => {
        const from = byDay.get(day.dayOfWeek);
        if (from === undefined) return { ...day };
        if (from.kind === "OFF") {
          return { ...day, kind: "OFF", startsAt: null, endsAt: null, breakMinutes: 0 };
        }
        return { ...from, breakMinutes: day.breakMinutes };
      }),
    );
  }

  /** One day's hours across the days already worked. **Days off stay off.** */
  function spreadFirstDay(): void {
    if (firstWorked === undefined) return;
    fill(
      days.map((day) =>
        day.kind === "OFF" ? { ...day } : { ...firstWorked, dayOfWeek: day.dayOfWeek },
      ),
    );
  }

  /** A colleague's whole week here, as a snapshot (S4) — a later change to theirs does not follow. */
  function copyFrom(userId: string): void {
    setCopying(true);
    readPattern(userId)
      .then((theirs) => fill(theirs.days.map((day) => ({ ...day }))))
      .catch((cause: unknown) => onError(describe(cause, "Could not read that person's week.")))
      .finally(() => setCopying(false));
  }

  const minutes = dirty ? pendingMinutes(days) : pattern.paidMinutes;
  const contract = pattern.contractedMinutes;
  const difference = contract === null ? null : minutes - contract;
  const locked = busy || copying;

  return (
    <Modal
      label="Every week"
      onClose={onClose}
      panelClassName={styles.everyDialog}
      testId="week-editor"
    >
      <div className={styles.everyHead} data-role={person.role}>
        <span className={styles.avatar} aria-hidden="true">
          {person.initials}
        </span>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Every week</h2>
          <p className={styles.dialogSub}>
            {person.name} ·{" "}
            {pattern.effectiveFrom === null
              ? "no regular hours saved yet"
              : `in force since ${pattern.effectiveFrom}`}
            {pattern.elsewhere.length > 0 &&
              ` · also works at ${pattern.elsewhere.join(", ")}, edited there`}
          </p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <div className={styles.everyBody}>
        <div className={styles.everySum} data-testid="week-total">
          <b>{hoursOf(minutes)}</b>
          <span>
            {contract === null
              ? "a week · no contracted hours on file"
              : `of ${hoursOf(contract)} a week`}
            {difference !== null && difference !== 0 && (
              <span data-testid="week-difference">
                {" · "}
                {hoursOf(Math.abs(difference))} {difference > 0 ? "over" : "under"}
              </span>
            )}
            {dirty && (
              <>
                {" · "}
                <em className={styles.unsaved} data-testid="week-unsaved">
                  not saved yet
                </em>
              </>
            )}
          </span>
          {contract !== null && contract > 0 && (
            <span className={styles.meter} aria-hidden="true">
              <i
                style={{
                  width: `${String(Math.min(100, Math.round((minutes / contract) * 100)))}%`,
                }}
              />
            </span>
          )}
        </div>

        {suggested && dirty && (
          <p className={styles.everyNote} data-testid="week-suggested">
            Filled in with the practice&rsquo;s usual working day. Adjust it if you need to &mdash;
            nothing is saved until you press Save.
          </p>
        )}

        {sources !== null && (
          <div className={styles.fillRow} data-testid="quick-fill">
            <span>Fill from</span>
            {sources.openingHours === null ? (
              // A button that could only produce an all-off week looks broken; say why.
              <span data-testid="no-branch-hours">
                &mdash; this location has no opening hours yet; they are set in System
              </span>
            ) : (
              <button
                type="button"
                className={styles.linkish}
                disabled={locked}
                onClick={fillOpeningHours}
                data-testid="fill-opening-hours"
              >
                this location&rsquo;s hours
              </button>
            )}
            {firstWorked !== undefined && (
              <>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className={styles.linkish}
                  disabled={locked}
                  onClick={spreadFirstDay}
                  data-testid="fill-spread"
                >
                  {WEEKDAY_NAMES[firstWorked.dayOfWeek] ?? "That day"}&rsquo;s hours all week
                </button>
              </>
            )}
            {sources.colleagues.length > 0 && (
              <>
                <span aria-hidden="true">·</span>
                {/*
                  An action, not a value — it goes straight back to the
                  placeholder, since "showing Sara's week" is not something the
                  form remembers once it is edited.
                */}
                <select
                  className={styles.linkSelect}
                  value=""
                  disabled={locked}
                  aria-label="Copy someone’s week"
                  onChange={(event) => {
                    if (event.target.value !== "") copyFrom(event.target.value);
                  }}
                  data-testid="fill-copy"
                >
                  <option value="">someone else&rsquo;s week&hellip;</option>
                  {sources.colleagues.map((colleague) => (
                    <option key={colleague.userId} value={colleague.userId}>
                      {colleague.name}
                    </option>
                  ))}
                </select>
              </>
            )}
            {/* The row's old note behind a ?, word for word (CS11) — hover and tap alike. */}
            <HelpTip label="About filling a week">Nothing is saved until you press Save.</HelpTip>
          </div>
        )}

        <ul className={styles.everyDays}>
          {days.map((day) => {
            const name = WEEKDAY_NAMES[day.dayOfWeek] ?? "";
            const isOpen = open === day.dayOfWeek;
            return (
              <li key={day.dayOfWeek} className={styles.everyDay} data-testid="day-row">
                <button
                  type="button"
                  className={isOpen ? `${styles.ew} ${styles.ewOpen}` : styles.ew}
                  data-card=""
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : day.dayOfWeek)}
                  data-testid="day-open"
                >
                  <span className={styles.ewDay}>{name}</span>
                  <span className={styles.ewValue} data-testid="day-value">
                    {day.kind === "OFF" ? (
                      <span className={styles.offWord}>Off</span>
                    ) : (
                      <>
                        <span
                          className={
                            day.kind === "TRAINING"
                              ? `${styles.pill} ${styles.pillTraining}`
                              : styles.pill
                          }
                          data-role={person.role}
                        >
                          {shiftText(day)}
                        </span>
                        {day.breakMinutes > 0 && (
                          <span className={styles.breakWord}>{day.breakMinutes} min break</span>
                        )}
                      </>
                    )}
                  </span>
                  <span className={styles.ewAction}>{isOpen ? "Done" : "Change"}</span>
                </button>

                {isOpen && (
                  <div className={styles.ewEditor}>
                    <div className={styles.kindChips} role="group" aria-label={`${name} is`}>
                      {STAFF_DAY_KINDS.map((kind) => (
                        <button
                          key={kind}
                          type="button"
                          className={styles.kindChip}
                          aria-pressed={day.kind === kind}
                          disabled={busy}
                          onClick={() => changeKind(day, kind)}
                          data-testid={`kind-${kind}`}
                        >
                          {KIND_LABELS[kind]}
                        </button>
                      ))}
                    </div>

                    {day.kind !== "OFF" && (
                      <div className={styles.timeFields}>
                        <label className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Starts
                            <RequiredMark />
                          </span>
                          <input
                            type="time"
                            className={`${styles.input} ${styles.timeInput}`}
                            value={day.startsAt ?? ""}
                            disabled={busy}
                            aria-label={`${name} start`}
                            onChange={(event) =>
                              edit(day.dayOfWeek, { startsAt: event.target.value || null })
                            }
                            data-testid="day-start"
                          />
                        </label>
                        <label className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Ends
                            <RequiredMark />
                          </span>
                          <input
                            type="time"
                            className={`${styles.input} ${styles.timeInput}`}
                            value={day.endsAt ?? ""}
                            disabled={busy}
                            aria-label={`${name} end`}
                            onChange={(event) =>
                              edit(day.dayOfWeek, { endsAt: event.target.value || null })
                            }
                            data-testid="day-end"
                          />
                        </label>
                        <NumberField
                          label="Break, minutes"
                          className={styles.field}
                          labelClassName={styles.fieldLabel}
                          inputClassName={`${styles.input} ${styles.timeInput}`}
                          value={breakTextOf(day)}
                          onChange={(next) => editBreak(day.dayOfWeek, next)}
                          rules={BREAK}
                          disabled={busy}
                          testId="day-break"
                        />
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <label className={styles.effectiveRow}>
          <span>Takes effect from</span>
          <input
            type="date"
            className={`${styles.input} ${styles.dateInput}`}
            value={effectiveFrom}
            disabled={busy}
            aria-label="Takes effect from"
            onChange={(event) => setEffectiveFrom(event.target.value)}
            data-testid="effective-from"
          />
        </label>
      </div>

      <div className={styles.dialogFoot}>
        {/*
          Undo reopens the last *saved* version (S1); Discard drops what is on
          screen. Two different actions, so they never share a button.
        */}
        {pattern.hasPreviousVersion && (
          <button
            type="button"
            className={`${styles.quiet} ${styles.footLead}`}
            disabled={busy}
            onClick={onRevert}
            data-testid="week-revert"
          >
            Undo the last saved change
          </button>
        )}
        {dirty && (
          <button
            type="button"
            className={styles.ghost}
            disabled={busy}
            onClick={() => fill(pattern.days)}
            data-testid="week-discard"
          >
            Discard changes
          </button>
        )}
        <button
          type="button"
          className={styles.primary}
          // The model is written only when the break text parses, so saving
          // with `2e` on screen would write what the box held before (QA20-13).
          disabled={busy || !dirty || breakBlocked}
          onClick={() => onSave(days, effectiveFrom)}
          data-testid="week-save"
        >
          Save regular hours
        </button>
      </div>
    </Modal>
  );
}

/** §6.4, for the unsaved total only. `OFF` is the one unpaid kind. */
function pendingMinutes(days: readonly PatternDay[]): number {
  return days.reduce((total, day) => {
    if (day.kind === "OFF" || day.startsAt === null || day.endsAt === null) return total;
    return total + Math.max(0, minutesOf(day.endsAt) - minutesOf(day.startsAt) - day.breakMinutes);
  }, 0);
}
